import * as DialogPrimitive from '@radix-ui/react-dialog'
import { ChevronLeft, ChevronRight, CircleX, ImageOff } from 'lucide-react'
import { type KeyboardEvent, useState } from 'react'
import { Dialog, DialogOverlay, DialogPortal } from '@/components/ui/dialog'
import { cn } from '@/lib/utils'

const controlClassName =
  'grid size-12 place-items-center rounded-xl bg-white text-[#1D1D1D] shadow-[0px_4px_8px_0px_rgba(113,128,150,0.16)] transition-opacity hover:opacity-90 cursor-pointer disabled:cursor-not-allowed disabled:opacity-40'

/** Consultation attachment viewer: one image at a time, arrows + thumbnails to move between them. */
export default function AttachmentPreview({ images, onClose }: { images: string[]; onClose: () => void }) {
  const [index, setIndex] = useState(0)
  const [failed, setFailed] = useState<ReadonlySet<string>>(new Set())

  const current = images[index]
  const hasMultiple = images.length > 1
  const goTo = (next: number) => setIndex(Math.min(Math.max(next, 0), images.length - 1))

  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'ArrowLeft') goTo(index - 1)
    if (event.key === 'ArrowRight') goTo(index + 1)
  }

  return (
    <Dialog open onOpenChange={open => !open && onClose()}>
      <DialogPortal>
        <DialogOverlay className='z-100 bg-[#24272B]/60 backdrop-blur-sm' />
        <DialogPrimitive.Content
          aria-describedby={undefined}
          onKeyDown={onKeyDown}
          className='fixed left-1/2 top-1/2 z-100 flex -translate-x-1/2 -translate-y-1/2 flex-col items-center gap-6 outline-hidden'
        >
          <DialogPrimitive.Title className='sr-only'>
            Attachment {index + 1} of {images.length}
          </DialogPrimitive.Title>

          <div className='relative h-[min(68vh,670px)] w-[min(90vw,1030px)] overflow-hidden rounded-3xl bg-white'>
            {failed.has(current) ? (
              <div className='flex size-full flex-col items-center justify-center gap-3 text-sm text-custom-neutral-600'>
                <ImageOff aria-hidden className='size-8' />
                This image couldn't be loaded.
              </div>
            ) : (
              <img
                key={current}
                src={current}
                alt={`Attachment ${index + 1} of ${images.length}`}
                onError={() => setFailed(previous => new Set(previous).add(current))}
                className='size-full object-contain'
              />
            )}

            <DialogPrimitive.Close
              aria-label='Close preview'
              className={cn(controlClassName, 'absolute right-4 top-4')}
            >
              <CircleX aria-hidden className='size-6' />
            </DialogPrimitive.Close>

            {hasMultiple && (
              <>
                <button
                  type='button'
                  aria-label='Previous image'
                  disabled={index === 0}
                  onClick={() => goTo(index - 1)}
                  className={cn(controlClassName, 'absolute left-4 top-1/2 -translate-y-1/2')}
                >
                  <ChevronLeft aria-hidden className='size-5' />
                </button>
                <button
                  type='button'
                  aria-label='Next image'
                  disabled={index === images.length - 1}
                  onClick={() => goTo(index + 1)}
                  className={cn(controlClassName, 'absolute right-4 top-1/2 -translate-y-1/2')}
                >
                  <ChevronRight aria-hidden className='size-5' />
                </button>
              </>
            )}
          </div>

          {hasMultiple && (
            <div className='flex max-w-[90vw] gap-3 overflow-x-auto p-1'>
              {images.map((image, imageIndex) => (
                <button
                  key={imageIndex}
                  type='button'
                  aria-label={`View image ${imageIndex + 1}`}
                  aria-current={imageIndex === index}
                  onClick={() => setIndex(imageIndex)}
                  className='h-26 w-28 shrink-0 overflow-hidden rounded-xl bg-white cursor-pointer'
                >
                  <img
                    src={image}
                    alt=''
                    className={cn(
                      'size-full object-cover transition-[filter]',
                      imageIndex === index ? 'brightness-100' : 'brightness-50 hover:brightness-75'
                    )}
                  />
                </button>
              ))}
            </div>
          )}
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  )
}
